import { Promise } from 'rsvp';
import Service, { inject as service } from '@ember/service';
import {
  convertNewLinesToBRs,
  stripTags
} from "../../utils/misc";

/**
 * Service for company related API calls.
 */
export default Service.extend({
  api: service(),
  store: service(),
  errorTracker: service('error-tracker'),
  session: service(),
  shData: service(),

  /**
   * Delete a note for a given freelancer
   * NOTE: - createdAboutUser must be populated.
   *
   * @param {Note} note
   * @return {Ember.RSVP.Promise}
   */
  deleteNote(note) {
    return this.api.delete(`notes/${note.id}`);
  },

  /**
   * Load all company notes for a specific seller.
   *
   * @param {String} subjectUser
   * @param {String=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
  loadNotesOnSubject(subjectUser, authorUser, opt_includes) {
    const options = {
      params: {},
    };

    if (opt_includes) {
      options.params['include'] = opt_includes;
    }

    if (authorUser.isAdmin) {
      return this.api.request(`users/${subjectUser.id}/admin-notes`, options).then(response => {
        this.shData.pushResponseToStore(response);
        return this.shData.getModelArrayFromResponse(response, 'Note');
      });
    }

    return this.api.request(`users/${subjectUser.id}/company-notes`, options).then(response => {
      this.shData.pushResponseToStore(response);
      return this.shData.getModelArrayFromResponse(response, 'Note');
    });
  },

  /**
   * Load all admin notes on a company
   *
   * @param {String} subjectUser
   * @param {String=} opt_includes
   * @return {Ember.RSVP.Promise}
   */
   loadAdminNotesOnCompany(company, opt_includes = null) {
    const options = {
      params: {},
    };

    if (opt_includes) {
      options.params['include'] = opt_includes;
    }

    return this.api.request(`companies/${company.id}/admin-notes`, options).then(response => {
      this.shData.pushResponseToStore(response);
      return this.shData.getModelArrayFromResponse(response, 'note')
    });
  },

  /**
   * Given a subject user, a note author user, and a new note, save that on the subject user.
   *
   * @param {User} subjectUser
   * @param {User} authorUser
   * @param {String} noteContent
   * @return {Ember.RSVP.Promise}
   */
  saveNoteOnSubject(subjectUser, authorUser, noteContent) {
    const safeContent = convertNewLinesToBRs(stripTags(noteContent));

    const options = {
      jsonData: {
        content: safeContent
      },
    };

    if (authorUser.isAdmin) {
      return this.api.post(`users/${subjectUser.id}/admin-notes`, options);
    }

    return this.api.post(`users/${subjectUser.id}/company-notes`, options);
  },

  updateNote(note, newNoteContent) {
    const safeContent = convertNewLinesToBRs(stripTags(newNoteContent));

    const options = {
      jsonData: {
        content: safeContent,
      },
    };

    return this.api.put(`notes/${note.id}`, options).then((response) => {
      this.shData.pushResponseToStore(response);
      return this.store.peekRecord('note', response.data.id);
    });
  },

  saveAdminNote(projectdigest, noteContent) {
    const safeContent = convertNewLinesToBRs(stripTags(noteContent));

    const options = {
      jsonData: {
        content: safeContent
      },
    };

    return this.api.post(`projectdigests/${projectdigest.id}/admin-notes`, options);
  },

  saveAdminNoteOnCompany(company, noteContent) {
    const safeContent = convertNewLinesToBRs(stripTags(noteContent));

    const options = {
      jsonData: {
        content: safeContent,
      },
    };

    return this.api.post(`companies/${company.id}/admin-notes`, options);
  },
});
